/**
 * UsageByWorkflowTable Component
 *
 * Displays a table showing token usage breakdown per orchestration.
 * Columns: Name, Total Tokens, Cost, Runs, Avg Tokens/Run
 *
 * Technical Design Reference: docs/technical/TD-AC-594-token-audit.md
 * Related Tickets:
 * - AC-594 (Epic: Token Audit System)
 * - AC-610 (Frontend: UsageByWorkflowTable component)
 * - AC-612 (Parent: UsageDashboard container)
 */

import React, { useState, useMemo } from 'react';
import { Workflow, AlertCircle, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import type { TokenUsageByWorkflow } from '../../types/tokenUsage';

export interface UsageByWorkflowTableProps {
  /** Token usage by workflow data from API */
  data: TokenUsageByWorkflow[] | null;
  /** Whether data is currently loading */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Callback when a workflow is clicked for drill-down (AC-622) */
  onWorkflowClick?: (orchestration: TokenUsageByWorkflow) => void;
  /** Optional filter context to show in title (e.g., project name when drilling down) */
  filterContext?: string;
}

/**
 * Sort direction for table columns.
 */
type SortDirection = 'asc' | 'desc' | null;

/**
 * Sortable column keys.
 */
type SortColumn = 'orchestration_name' | 'total_tokens' | 'total_cost' | 'run_count' | 'avg_tokens_per_run';

/**
 * Format a number with thousands separator using locale formatting.
 */
const formatNumber = (n: number): string => {
  return n.toLocaleString('en-US');
};

/**
 * Format a number as currency with $ symbol.
 * Uses adaptive precision: 4 decimal places for amounts under $1,
 * 2 decimal places for larger amounts.
 */
const formatCurrency = (n: number): string => {
  const decimalPlaces = n < 1 && n > 0 ? 4 : 2;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}`;
};

/**
 * Loading skeleton for the table.
 */
const LoadingSkeleton: React.FC = () => (
  <div
    role="status"
    aria-busy="true"
    aria-label="Loading workflow usage data"
    data-testid="orchestration-table-loading"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 animate-pulse"
  >
    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4" />
    <div className="space-y-3">
      {/* Header row */}
      <div className="flex space-x-4 pb-2 border-b border-gray-200 dark:border-gray-700">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded flex-1" />
        ))}
      </div>
      {/* Data rows */}
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex space-x-4">
          {[1, 2, 3, 4, 5].map((j) => (
            <div key={j} className="h-4 bg-gray-200 dark:bg-gray-700 rounded flex-1" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

/**
 * Error state component.
 */
const ErrorState: React.FC<{ message: string }> = ({ message }) => (
  <div
    role="alert"
    data-testid="orchestration-table-error"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6"
  >
    <div className="flex items-center text-red-600 mb-4">
      <AlertCircle data-testid="orchestration-error-icon" className="w-5 h-5 mr-2" />
      <span className="font-medium">Error loading workflow data</span>
    </div>
    <p className="text-gray-600 dark:text-gray-400">{message}</p>
  </div>
);

/**
 * Empty state when no data is available.
 */
const EmptyState: React.FC<{ filterContext?: string }> = ({ filterContext }) => (
  <div
    data-testid="orchestration-table-empty"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 text-center"
  >
    <Workflow className="w-12 h-12 text-gray-400 mx-auto mb-3" />
    <p className="text-gray-500 dark:text-gray-400">
      No workflow usage data{filterContext ? ` in ${filterContext}` : ''} available
    </p>
    <p className="text-sm text-gray-400 mt-1">
      {filterContext
        ? `No workflows have recorded usage in ${filterContext} for this period`
        : 'Data will appear once workflows are executed'}
    </p>
  </div>
);

/**
 * Sort indicator icon component.
 */
const SortIcon: React.FC<{ direction: SortDirection }> = ({ direction }) => {
  if (direction === 'asc') {
    return <ChevronUp className="w-4 h-4" />;
  }
  if (direction === 'desc') {
    return <ChevronDown className="w-4 h-4" />;
  }
  return <ChevronsUpDown className="w-4 h-4 text-gray-400" />;
};

/**
 * Sortable table header component.
 */
interface SortableHeaderProps {
  label: string;
  column: SortColumn;
  sortColumn: SortColumn | null;
  sortDirection: SortDirection;
  onSort: (column: SortColumn) => void;
  align?: 'left' | 'right';
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  column,
  sortColumn,
  sortDirection,
  onSort,
  align = 'left',
}) => {
  const isActive = sortColumn === column;
  const direction = isActive ? sortDirection : null;

  return (
    <th
      className={`px-4 py-3 text-${align} text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
      onClick={() => onSort(column)}
    >
      <div className={`flex items-center space-x-1 ${align === 'right' ? 'justify-end' : ''}`}>
        <span>{label}</span>
        <SortIcon direction={direction} />
      </div>
    </th>
  );
};

/**
 * UsageByWorkflowTable component displays token usage breakdown per orchestration.
 */
const UsageByWorkflowTable: React.FC<UsageByWorkflowTableProps> = ({
  data,
  loading = false,
  error = null,
  onWorkflowClick,
  filterContext,
}) => {
  // Sort state
  const [sortColumn, setSortColumn] = useState<SortColumn | null>('total_cost');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Handle sort column click
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Cycle through: desc -> asc -> null
      if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else if (sortDirection === 'asc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!data || !sortColumn || !sortDirection) {
      return data || [];
    }

    return [...data].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [data, sortColumn, sortDirection]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!data || data.length === 0) {
      return { tokens: 0, cost: 0, runs: 0 };
    }
    return {
      tokens: data.reduce((sum, d) => sum + d.total_tokens, 0),
      cost: data.reduce((sum, d) => sum + d.total_cost, 0),
      runs: data.reduce((sum, d) => sum + d.run_count, 0),
    };
  }, [data]);

  // Show loading state
  if (loading) {
    return <LoadingSkeleton />;
  }

  // Show error state
  if (error) {
    return <ErrorState message={error} />;
  }

  // Show empty state when no data
  if (!data || data.length === 0) {
    return <EmptyState filterContext={filterContext} />;
  }

  return (
    <div
      data-testid="usage-by-orchestration-table"
      className="bg-white dark:bg-cap-dark rounded-lg shadow overflow-hidden"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Workflow data-testid="orchestration-table-icon" className="w-5 h-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">
              Usage by Workflow{filterContext && <span className="text-primary-600"> in {filterContext}</span>}
            </h3>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {data.length} orchestration{data.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table
          data-testid="orchestration-data-table"
          className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
        >
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <SortableHeader
                label="Workflow"
                column="orchestration_name"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="Total Tokens"
                column="total_tokens"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortableHeader
                label="Cost"
                column="total_cost"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortableHeader
                label="Runs"
                column="run_count"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortableHeader
                label="Avg Tokens/Run"
                column="avg_tokens_per_run"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                align="right"
              />
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700">
            {sortedData.map((orchestration, index) => (
              <tr
                key={orchestration.orchestration_id}
                data-testid={`orchestration-row-${index}`}
                className="hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors"
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <div
                    className={`flex items-center ${onWorkflowClick ? 'cursor-pointer hover:text-primary-600' : ''}`}
                    onClick={() => onWorkflowClick?.(orchestration)}
                  >
                    <Workflow className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="font-medium text-gray-900 dark:text-warm-white">
                      {orchestration.orchestration_name}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-gray-900 dark:text-warm-white">
                  {formatNumber(orchestration.total_tokens)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right font-medium text-gray-900 dark:text-warm-white">
                  {formatCurrency(orchestration.total_cost)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600 dark:text-gray-400">
                  {formatNumber(orchestration.run_count)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600 dark:text-gray-400">
                  {formatNumber(Math.round(orchestration.avg_tokens_per_run))}
                </td>
              </tr>
            ))}
          </tbody>
          {/* Footer with totals */}
          <tfoot className="bg-gray-50 dark:bg-gray-900">
            <tr data-testid="orchestration-totals-row">
              <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900 dark:text-warm-white">
                Total ({data.length} orchestration{data.length !== 1 ? 's' : ''})
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right font-bold text-gray-900 dark:text-warm-white">
                {formatNumber(totals.tokens)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right font-bold text-gray-900 dark:text-warm-white">
                {formatCurrency(totals.cost)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right font-bold text-gray-900 dark:text-warm-white">
                {formatNumber(totals.runs)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right font-bold text-gray-900 dark:text-warm-white">
                {totals.runs > 0 ? formatNumber(Math.round(totals.tokens / totals.runs)) : '-'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Summary */}
      <div
        data-testid="orchestration-table-summary"
        className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center justify-between text-sm">
          <div className="text-gray-500 dark:text-gray-400">
            Showing {data.length} orchestration{data.length !== 1 ? 's' : ''} with token usage
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-500 dark:text-gray-400">
              Avg Cost/Run:{' '}
              <span className="font-medium text-gray-900 dark:text-warm-white">
                {totals.runs > 0 ? formatCurrency(totals.cost / totals.runs) : '-'}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageByWorkflowTable;
